import { useState } from 'react';

import { LayoutDashboard } from 'lucide-react';

import type { DashboardSlice } from '@/api/schema';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { ErrorState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { StatStrip } from '@/components/ui/StatStrip';

import { labelledBy, SOURCE_LABEL, STATUS_LABEL } from '@/features/candidates/labels';

import { useDashboard } from '../api';
import { Area, Bars, Donut, Funnel } from '../components/Charts';
import styles from './DashboardPage.module.css';

/** Ranges people actually ask for, rather than two date pickers. */
const RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last year' },
  { value: '1825', label: 'All time' },
];

function isoDaysAgo(days: number): string {
  const at = new Date();
  at.setDate(at.getDate() - days);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;
}

/**
 * Raw enum values into the words the rest of the portal uses.
 *
 * The server groups by the stored value, which is right: it should not be inventing
 * English. But `candidate_applied` and `whatsapp_community` are not what any other
 * screen calls those things, and a dashboard that names them differently is a
 * dashboard nobody can line up against the list it came from.
 */
function humanise(rows: DashboardSlice[], labels: Record<string, string>): DashboardSlice[] {
  const label = labelledBy(labels);
  return rows.map((row) => ({ ...row, label: label(row.label) }));
}

function todayIso(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The dashboard.
 *
 * One request drives every panel, and one range drives that request. A screen whose
 * funnel covers this month and whose donut covers all time is one nobody can reconcile
 * with anything.
 */
export function DashboardPage() {
  const [days, setDays] = useState('90');
  const dashboard = useDashboard(isoDaysAgo(Number(days)), todayIso());

  const data = dashboard.data;

  return (
    <Page>
      <PageHeader
        title="Dashboard"
        description="The register at a glance."
        actions={
          <Select
            aria-label="Date range"
            value={days}
            onChange={(event) => {
              setDays(event.target.value);
            }}
            options={RANGES}
          />
        }
      />

      {dashboard.isError ? (
        <ErrorState
          message="The dashboard did not load."
          onRetry={() => {
            void dashboard.refetch();
          }}
        />
      ) : !data ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading the dashboard" />
        </div>
      ) : (
        <>
          <StatStrip
            items={[
              { label: 'Candidates', value: data.summary.total },
              { label: 'In screening', value: data.summary.in_screening },
              { label: 'In the ATS', value: data.summary.in_ats },
              { label: 'Rejected', value: data.summary.rejected },
              { label: 'Meetings ahead', value: data.summary.meetings_upcoming },
            ]}
          />

          <div className={styles.grid}>
            <Panel title="Arriving each week" icon={<LayoutDashboard size={15} />}>
              <Area points={data.weekly} empty="Nobody arrived in this range." />
            </Panel>

            <Panel title="Screening funnel">
              <Funnel steps={data.funnel} empty="Nobody is in screening in this range." />
            </Panel>

            <Panel title="By status">
              <Bars
                rows={humanise(data.by_status, STATUS_LABEL)}
                empty="No candidates in this range."
              />
            </Panel>

            <Panel title="Where they came from">
              <Donut
                rows={humanise(data.by_source, SOURCE_LABEL)}
                empty="No candidates in this range."
              />
            </Panel>

            <Panel title="By sector">
              <Bars rows={data.by_department} empty="No candidates in this range." />
            </Panel>

            <Panel title="Where they are going">
              <Bars rows={data.by_destination} empty="No candidates in this range." />
            </Panel>
          </div>
        </>
      )}
    </Page>
  );
}
